import React, { useEffect, useState } from "react";
import { toast } from "sonner";

const inp = "bg-surface border border-edge px-3 py-2 font-term text-[13px] text-coolwhite outline-none focus:border-nettred w-full";
const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const EMPTY = { name: "", domains: "", queries: "", active: true };

function ClientForm({ initial, onSubmit, submitLabel, testid }) {
  const [d, setD] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!d.name.trim()) return;
    setBusy(true);
    try { await onSubmit({ name: d.name, domains: lines(d.domains), queries: lines(d.queries).slice(0, 10), active: d.active }); }
    finally { setBusy(false); }
  };
  return (
    <form data-testid={testid} onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <input data-testid={`${testid}-name`} className={`${inp} md:col-span-2`} placeholder="Kundenavn, f.eks. Butikk AS"
        value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
      <label className="block">
        <span className="font-display tracking-[0.18em] text-[10px] uppercase text-dim">// egne domener (ett per linje)</span>
        <textarea data-testid={`${testid}-domains`} rows={5} className={`${inp} mt-1 resize-y`} placeholder={"butikk.no\nblogg.butikk.no"}
          value={d.domains} onChange={(e) => setD({ ...d, domains: e.target.value })} />
      </label>
      <label className="block">
        <span className="font-display tracking-[0.18em] text-[10px] uppercase text-dim">// ukentlige spørringer (én per linje, maks 10)</span>
        <textarea data-testid={`${testid}-queries`} rows={5} className={`${inp} mt-1 resize-y`} placeholder={"Beste nettbutikk for X i Norge?\nHvem selger Y billigst?"}
          value={d.queries} onChange={(e) => setD({ ...d, queries: e.target.value })} />
      </label>
      <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input data-testid={`${testid}-active`} type="checkbox" checked={d.active} onChange={(e) => setD({ ...d, active: e.target.checked })} className="h-4 w-4 accent-nettred" />
          <span className="font-display tracking-[0.18em] text-[11px] uppercase text-coolwhite/90">{d.active ? "Aktiv · kjøres hver mandag" : "Pauset"}</span>
        </label>
        <button data-testid={`${testid}-submit`} disabled={busy}
          className="bg-nettred text-space font-display tracking-[0.22em] text-[11px] uppercase px-5 py-2 hover:bg-amber hover:text-space transition-colors disabled:opacity-50">
          {busy ? "…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

const toDraft = (c) => ({ name: c.name, domains: (c.domains || []).join("\n"), queries: (c.queries || []).join("\n"), active: c.active });

function ClientRow({ c, ax, reload, fmtErr }) {
  const [edit, setEdit] = useState(false);
  const [running, setRunning] = useState(false);
  const save = async (body) => {
    try { await ax.put(`/admin/clients/${c.id}`, body); toast.success("Lagret."); setEdit(false); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const run = async () => {
    if (!window.confirm(`Kjør ${c.queries.length} spørringer for ${c.name} nå?`)) return;
    setRunning(true);
    try { const { data } = await ax.post(`/admin/clients/${c.id}/run`); toast.success(`${data.ran} snapshots lagt til for ${c.name}.`); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Slette ${c.name} og alle kundens snapshots?`)) return;
    try { await ax.delete(`/admin/clients/${c.id}`); reload(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const accent = c.active ? "#10b981" : "#a3acb9";
  const btn = "text-[11px] font-display tracking-[0.18em] uppercase px-3 py-2 border transition-colors";
  return (
    <div data-testid="client-row" className="intel-panel p-4" style={{ "--panel-accent": accent, borderLeftColor: accent }}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-bold text-coolwhite uppercase tracking-wide">{c.name}</div>
          <div className="font-term text-[11.5px] text-dim mt-0.5">
            {c.domains.length} domener · {c.queries.length} spørringer · {c.batches || 0} batcher · {c.active ? "aktiv" : "pauset"}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="client-run" onClick={run} disabled={running} className={`${btn} border-l3 text-l3 hover:bg-l3 hover:text-space disabled:opacity-50`}>{running ? "kjører…" : "kjør nå"}</button>
          <button data-testid="client-edit" onClick={() => setEdit(!edit)} className={`${btn} border-amber text-amber hover:bg-amber hover:text-space`}>{edit ? "lukk" : "rediger"}</button>
          <button data-testid="client-delete" onClick={remove} className={`${btn} border-edge text-coolwhite/80 hover:border-nettred hover:text-nettred`}>slett</button>
        </div>
      </div>
      {!edit && c.domains.length > 0 && (
        <div className="mt-2 font-term text-[12px] text-coolwhite/80">{c.domains.join(" · ")}</div>
      )}
      {edit && <div className="mt-4"><ClientForm initial={toDraft(c)} onSubmit={save} submitLabel="Lagre" testid="client-edit-form" /></div>}
    </div>
  );
}

export const ClientsTab = ({ ax, fmtErr }) => {
  const [items, setItems] = useState([]);
  const [formKey, setFormKey] = useState(0);
  const load = async () => {
    try { const { data } = await ax.get("/admin/clients"); setItems(data); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (body) => {
    try { await ax.post("/admin/clients", body); toast.success(`${body.name} lagt til.`); setFormKey((k) => k + 1); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="clients-tab">
      <h2 className="font-display font-bold text-2xl text-coolwhite uppercase">Kunder · AI Mention Tracking</h2>
      <p className="mt-1 font-term text-[13px] text-dim max-w-3xl">
        Hver kunde får egne spørringer og egne domener. Aktive kunder kjøres automatisk hver mandag kl. 08:00 sammen med Nettpilot,
        og du får et AI-varsel med kundens PDF vedlagt. Velg kunden i «// mentions» for å se diff og laste ned rapporten.
      </p>
      <div className="intel-panel p-5 mt-6" style={{ "--panel-accent": "#38bdf8", borderLeftColor: "#38bdf8" }}>
        <div className="font-display tracking-[0.2em] text-[11px] uppercase text-l2 mb-3">// NY KUNDE</div>
        <ClientForm key={formKey} initial={EMPTY} onSubmit={create} submitLabel="+ Legg til kunde" testid="client-new-form" />
      </div>
      <div className="mt-6 space-y-3">
        {items.length === 0 && <div className="font-term text-dim">// ingen kunder ennå</div>}
        {items.map((c) => <ClientRow key={c.id} c={c} ax={ax} reload={load} fmtErr={fmtErr} />)}
      </div>
    </div>
  );
};
